<template>
    <div class="rounded-lgr border bg-white p-7.5" :class="isEditing ? 'border-valence-primary-400 ring-2 ring-valence-primary-500/20' : 'border-valence-grey-250'">
        <template v-if="!isEditing">
            <div class="flex flex-col gap-5">
                <div class="flex items-center justify-between gap-4">
                    <div class="flex items-center gap-2.5 min-w-0">
                        <BaseAvatar size="md" theme="primary" class="shadow-[inset_0_0_0_1.75px_#0003]" aria-hidden="true" translate="no">
                            <span class="nadia-chat-bold">
                                {{ userInitials }}
                            </span>
                        </BaseAvatar>
                        <div class="display-serif text-valence-grey-850" translate="no">{{ displayName }}</div>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                        <span v-if="perspectiveTitle" class="inline-flex items-center gap-1.5 rounded-full bg-valence-grey-50 px-2.5 py-1 body-small text-valence-grey-850">
                            <ArchetypeDot :letters="perspectiveLetters" class="size-3.5" />
                            {{ perspectiveTitle }}
                        </span>
                        <BaseButton v-if="editable" theme="neutral" icon-only aria-label="Edit basic info" title="Edit basic info" @click="isEditing = true">
                            <i class="bi bi-pencil text-sm/none" aria-hidden="true"></i>
                        </BaseButton>
                    </div>
                </div>

                <MarkdownText v-if="summary" :text="summary" class="body-small text-valence-grey-800" />

                <HorizontalDivider class="my-1" />

                <div class="grid gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
                    <div v-for="field in allFields" :key="field.key" class="info-field">
                        <div class="info-field-label">{{ field.label }}</div>
                        <div class="info-field-value">
                            {{ field.value }}<span v-if="field.suffix" class="ml-1 text-valence-grey-500">{{ field.suffix }}</span>
                        </div>
                    </div>
                    <slot name="extra-fields" :fields="facetFields">
                        <div v-for="field in facetFields" :key="field.facet_id" class="info-field">
                            <div class="info-field-label">{{ field.facet_name }}</div>
                            <div class="info-field-value">{{ field.value }}</div>
                        </div>
                    </slot>
                </div>
                <div v-if="lastSyncedLabel" class="flex items-center gap-1.5 details text-valence-grey-500">
                    <i class="bi bi-arrow-repeat text-xs" aria-hidden="true"></i>
                    HR data last synced {{ lastSyncedLabel }}
                </div>
            </div>
        </template>
        <template v-else>
            <ProfileEditForm
                :existing-public-profile="existingPublicProfile"
                :basic-info="basicInfo"
                :hris-data="hrisData"
                :default-profile-access="defaultProfileAccess"
                @saved="onSaved"
                @cancelled="isEditing = false" />
        </template>
    </div>
</template>

<script setup lang="ts">
import type { HrisData, OrgTreePerson } from "~vue/appTypes";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import ProfileEditForm from "~vue/components/ProfileEditForm.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import type { FacetValue } from "~vue/types/connection";
import { type ExistingPublicProfile } from "~vue/types/profile";
import { getFirstLastInitials } from "~vue/utils/stringUtils";
import { computed, ref } from "vue";

import BaseButton from "./BaseButton.vue";
import HorizontalDivider from "./HorizontalDivider.vue";

const props = withDefaults(
    defineProps<{
        basicInfo: {
            full_name: string;
            role: string | null;
            team: string | null;
            level: string | null;
            organization: string | null;
        };
        editable?: boolean;
        existingPublicProfile?: ExistingPublicProfile | null;
        hrisData?: HrisData | null;
        manager?: OrgTreePerson | null;
        directReports?: OrgTreePerson[];
        perspectiveLetters?: string | null;
        facetFields?: FacetValue[];
        defaultProfileAccess?: string;
        summary?: string;
    }>(),
    {
        hrisData: null,
        summary: "",
        manager: null,
        directReports: () => [],
        perspectiveLetters: null,
        facetFields: () => [],
    },
);

const emit = defineEmits<{
    saved: [];
}>();

interface FieldEntry {
    key: string;
    label: string;
    value: string;
    suffix?: string;
}

const tenureLabel = computed(() => {
    if (!props.hrisData?.hire_date) {
        return null;
    }
    const hire = new Date(props.hrisData.hire_date);
    const now = new Date();
    let years = now.getFullYear() - hire.getFullYear();
    let months = now.getMonth() - hire.getMonth();
    if (months < 0) {
        years--;
        months += 12;
    }
    if (years > 0 && months > 0) {
        return `${years}y ${months}mo`;
    }
    if (years > 0) {
        return `${years} year${years === 1 ? "" : "s"}`;
    }
    if (months > 0) {
        return `${months} month${months === 1 ? "" : "s"}`;
    }
    return "Less than a month";
});

const startDateLabel = computed(() => {
    if (!props.hrisData?.start_date) {
        return null;
    }
    const date = new Date(props.hrisData.start_date);
    return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
});

const lastSyncedLabel = computed(() => {
    if (!props.hrisData?.last_synced_at) {
        return null;
    }
    const synced = new Date(props.hrisData.last_synced_at);
    const now = new Date();
    const diffMs = now.getTime() - synced.getTime();
    const diffMins = Math.floor(diffMs / 60_000);

    if (diffMins < 1) {
        return "just now";
    }
    if (diffMins < 60) {
        return `${diffMins}m ago`;
    }
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
        return `${diffHours}h ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) {
        return "yesterday";
    }
    return `${diffDays}d ago`;
});

const teamLabel = computed(() => (props.hrisData?.department && !props.existingPublicProfile?.division ? "Department" : "Team"));

const directReportsValue = computed(() => {
    const count = props.directReports.length;
    return count > 0 ? `${count} direct report${count === 1 ? "" : "s"}` : null;
});

const allFields = computed(() => {
    const role = props.existingPublicProfile?.title || props.hrisData?.job_title || props.basicInfo?.role;
    const team = props.existingPublicProfile?.division || props.hrisData?.department || props.basicInfo?.team;

    const candidates: (FieldEntry | null)[] = [
        role ? { key: "title", label: "Role", value: role } : null,
        team ? { key: "team", label: teamLabel.value, value: team } : null,
        props.basicInfo?.organization ? { key: "organization", label: "Organization", value: props.basicInfo.organization } : null,
        props.basicInfo?.level ? { key: "level", label: "Level", value: props.basicInfo.level } : null,
        props.existingPublicProfile?.email ? { key: "email", label: "Email", value: props.existingPublicProfile.email } : null,
        tenureLabel.value ? { key: "tenure", label: "Tenure", value: tenureLabel.value } : null,
        startDateLabel.value ? { key: "start_date", label: "Start Date", value: startDateLabel.value } : null,
        props.manager ? { key: "manager", label: "Manager", value: props.manager.name, suffix: props.manager.title ? `· ${props.manager.title}` : undefined } : null,
        directReportsValue.value ? { key: "direct_reports", label: "Direct Reports", value: directReportsValue.value } : null,
    ];

    return candidates.filter((f): f is FieldEntry => f !== null);
});

const displayName = computed(() => props.existingPublicProfile?.name || props.basicInfo?.full_name || "Unknown");
const userInitials = computed(() => getFirstLastInitials(displayName.value));

const perspectiveTitle = computed(() => (props.perspectiveLetters ? PERSPECTIVE_TYPES[props.perspectiveLetters]?.title : undefined));

const isEditing = ref(false);

function onSaved(): void {
    isEditing.value = false;
    emit("saved");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.info-field {
    @apply flex flex-col gap-1 min-w-0;
}

.info-field-label {
    @apply scaffold-body-small text-valence-grey-750;
}

.info-field-value {
    @apply body-semibold text-valence-grey-800 wrap-break-word;
}
</style>
